import { useTranslation } from 'react-i18next';
import styles from './ListOfBanksSelector.module.css';
import { useEffect, useState } from 'react';
import findListOfBanks , { Institution } from '../../../api/screening/income-verification/find-list-of-banks/findListOfBanks';
import SkeletonComponent from '../../../components/SkeletonComponent';
import { SelectNew } from '../select-new';
import { Option } from '../select-new/SelectNew';
import { BankOptionTransformer } from '../../molecules/bank-option-transformer';
import IconWithText from '../../atoms/typography/IconWithText';
import ErrorWarningLineIcon from '../../atoms/icons/ErrorWarningLineIcon';
import { FontSizeEnum } from '../../enums';
import Notifications from '../notifications/Notifications';

interface ListOfBanksSelectorProps {
    countryCode: string|null;
    name?: string;
    required?: boolean;
    onChange?: (value: string) => void;
}

const ListOfBanksSelector = (
    {
        countryCode,
        name,
        required = false,
        onChange
    }: ListOfBanksSelectorProps) => {
    const { t } = useTranslation();

    const [selectedItem, setSelectedItem] = useState<string>(null);
    const [options, setOptions] = useState<Option[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        fetchInstitutions();
    }, [countryCode]);

    const fetchInstitutions = async () => {
        setIsLoading(true);
        setOptions([]);

        try {
            if (countryCode) {
                const response = await findListOfBanks(countryCode);

                const transformedOptions = response.values.map((institution: Institution): Option => ({
                    label: (
                        <BankOptionTransformer
                            name={institution.name}
                            logoUrl={institution.logoUrl}
                        />
                    ),
                    value: institution.id,
                }));

                setOptions(transformedOptions);
            }
        } catch (error) {
            setOptions([
                {
                    label:
                        <div className={styles.iconWithText}>
                            <IconWithText
                                iconDisplay='inline'
                                icon={<ErrorWarningLineIcon />}
                                fontSize={FontSizeEnum.Md}>
                                {t('screening.incomeVerification.provideTransactions.listOfBanksSelector.errorText')}
                            </IconWithText>
                        </div>,
                    value: null
                }
            ]);
            Notifications.add({
                type: 'failed',
                message: t('api.request.failed')
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleInstitutionChange = (selectedOptions: Option[]) => {
        if (selectedOptions.length === 0) {
            setSelectedItem(null);
            onChange?.(null);
            return;
        }

        setSelectedItem(selectedOptions[0].value);
        onChange?.(selectedOptions[0].value);
    };

    return isLoading ? (
        <SkeletonComponent height={40} />
    ) : (
        <div className={styles.listOfBanksSelector} >
            <SelectNew
                name={name}
                placeholder={t('screening.incomeVerification.provideTransactions.listOfBanksSelector.placeHolder')}
                required={required}
                options={options}
                selected={selectedItem}
                hasInputField
                optionNotFoundText={t('screening.incomeVerification.provideTransactions.listOfBanksSelector.optionNotFoundText')}
                onChange={handleInstitutionChange}
            />
        </div>
    );
}
export default ListOfBanksSelector;
